import posthog from 'posthog-js';
import { AuthUser } from '../api/auth';
import { AnalyticsEventName } from './events';

declare global {
  interface Window {
    __SPOTDOVE_PUBLIC_CONFIG__?: {
      posthogKey?: string;
      posthogHost?: string;
    };
  }
}

const readPublicConfig = (): { key: string; host: string } => {
  const runtime =
    typeof window !== 'undefined' ? window.__SPOTDOVE_PUBLIC_CONFIG__ : undefined;

  const key = runtime?.posthogKey || process.env.PUBLIC_POSTHOG_KEY || '';
  const host =
    runtime?.posthogHost ||
    process.env.PUBLIC_POSTHOG_HOST ||
    'https://us.i.posthog.com';

  return { key, host };
};

let initialized = false;

export const isAnalyticsEnabled = (): boolean => Boolean(readPublicConfig().key);

/** Initialize PostHog once. No-op when no project key is configured. */
export const initPostHog = (): boolean => {
  const { key, host } = readPublicConfig();

  if (initialized || !key || typeof window === 'undefined') {
    return initialized;
  }

  posthog.init(key, {
    api_host: host,
    person_profiles: 'identified_only',
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: false,
    disable_session_recording: true,
    persistence: 'localStorage+cookie',
  });

  initialized = true;
  return true;
};

export const trackEvent = (
  event: AnalyticsEventName,
  properties?: Record<string, string | number | boolean | null | undefined>,
): void => {
  if (!initPostHog()) {
    return;
  }

  const cleaned = properties
    ? Object.fromEntries(
        Object.entries(properties).filter(([, value]) => value !== undefined && value !== null),
      )
    : undefined;

  posthog.capture(event, cleaned);
};

export const capturePageview = (pathname: string): void => {
  if (!initPostHog()) {
    return;
  }

  const url = `${window.location.origin}${pathname}${window.location.search}`;

  posthog.capture('$pageview', {
    $current_url: url,
    path: pathname,
  });
};

export const identifyUser = (user: AuthUser): void => {
  if (!initPostHog()) {
    return;
  }

  posthog.identify(user.id, {
    email: user.email,
    name: user.name,
    plan: user.plan,
    has_billing: user.hasBilling,
    created_at: user.createdAt,
  });
};

export const resetAnalyticsUser = (): void => {
  if (!initialized) {
    return;
  }

  posthog.reset();
};

/** @internal Exposed for tests only. */
export const getPostHogClient = (): typeof posthog => posthog;
